// src/core/matrixSmoother.js
//
// Smooths MediaPipe's column-major transformation matrix to remove jitter,
// then hands back a Matrix4 ready to pose both the occluder (M) and glasses
// (M·A). Decompose → One-Euro filter translation + quaternion → recompose.
//
// This is the ONLY smoothing in the new architecture. There is no hand-built
// basis (the source of the earlier flip bugs), so the quaternion comes straight
// from the matrix and we only stabilise its sign for filter continuity.

import { Matrix4, Vector3, Quaternion } from 'three';
import { OneEuroFilter } from './oneEuroFilter.js';

function mapResponsiveness(r) {
  const c = Math.max(0, Math.min(1, r));
  // Extended range for snappier tracking. The top end is pushed harder so the
  // glasses keep up with fast head motion: at 100% the filter is effectively a
  // passthrough (very high cutoff + beta). Lower values still give smooth,
  // stable tracking. Raising mincutoff/beta ceilings makes "fast" actually fast.
  return { mincutoff: 1.0 + c * (30.0 - 1.0), beta: 0.02 + c * (3.0 - 0.02), dcutoff: 1.0 };
}

const qs = new URLSearchParams(window.location.search);
const num = (k, fb) => { const v = parseFloat(qs.get(k)); return Number.isFinite(v) ? v : fb; };

export class MatrixSmoother {
  constructor() {
    this._resp = num('resp', 0.90);
    this._filterOn = !qs.has('nofilter');
    // Motion prediction ("lead"), in seconds. Every estimator in the chain —
    // MediaPipe's internal landmark smoothing/ROI tracking and our own slerp —
    // is a low-pass filter, so during a fast head turn the pose we get describes
    // where the head WAS a few tens of ms ago; it catches up when the head
    // stops. Extrapolating the pose forward by the filtered velocity × lead
    // cancels that group delay. ?lead=40 (ms). 0 = off.
    this._leadSec = Math.max(0, num('lead', 0)) / 1000;
    this._rawPosPrev = new Vector3();
    this._rawQuatPrev = new Quaternion();
    this._vel = new Vector3();          // filtered linear velocity (cm/s)
    this._dq = new Quaternion();        // filtered per-FRAME rotation delta (raw)
    this._dtAvg = 1 / 30;               // filtered frame interval (s)
    this._qTmp = new Quaternion();
    this._qIdentity = new Quaternion();
    this._posPred = new Vector3();
    this._qPred = new Quaternion();
    this._build();

    this._raw = new Matrix4();
    this._out = new Matrix4();
    this._pos = new Vector3();
    this._quat = new Quaternion();
    this._scale = new Vector3();
    this._quatPrev = new Quaternion();
    this._qOut = new Quaternion();
    this._posOut = new Vector3();
    this._lastTime = 0;
    this._hasPrev = false;
    this._loggedFirst = false;
  }

  _build() {
    const o = mapResponsiveness(this._resp);
    const rot = { mincutoff: o.mincutoff * 0.85, beta: o.beta * 1.25, dcutoff: 1.0 };
    // Translation is smoothed via adaptive lerp in update(); scale via filters.
    this.sx = new OneEuroFilter(o); this.sy = new OneEuroFilter(o); this.sz = new OneEuroFilter(o);
    // Rotation is smoothed via adaptive slerp in update(), not component filters.
    console.log(`[MatrixSmoother] resp=${this._resp.toFixed(2)} filter=${this._filterOn ? 'ON' : 'OFF'}`);
  }

  setResponsiveness(r) { this._resp = Math.max(0, Math.min(1, r)); this._build(); this._hasPrev = false; }
  setLead(ms) { this._leadSec = Math.max(0, ms) / 1000; }
  getLead() { return this._leadSec * 1000; }
  setFilterEnabled(on) { this._filterOn = on; this._build(); this._hasPrev = false; }
  getResponsiveness() { return this._resp; }
  isFilterEnabled() { return this._filterOn; }

  update(raw16, timeSec) {
    this._raw.fromArray(raw16);
    this._raw.decompose(this._pos, this._quat, this._scale);

    if (!this._loggedFirst) {
      console.log('[MatrixSmoother] first translation (cm):',
        this._pos.x.toFixed(2), this._pos.y.toFixed(2), this._pos.z.toFixed(2),
        ' scale:', this._scale.x.toFixed(3));
      this._loggedFirst = true;
    }

    // Sign-stabilise quaternion for continuity (q and -q are the same rotation).
    if (this._hasPrev) {
      const d = this._quat.x*this._quatPrev.x + this._quat.y*this._quatPrev.y
              + this._quat.z*this._quatPrev.z + this._quat.w*this._quatPrev.w;
      if (d < 0) { this._quat.x*=-1; this._quat.y*=-1; this._quat.z*=-1; this._quat.w*=-1; }
    }

    if (!this._filterOn) {
      const dt0 = Math.max(1e-3, timeSec - this._lastTime);
      this._qOut.copy(this._quat);
      this._posOut.copy(this._pos);
      this._updateVelocity(dt0);
      this._quatPrev.copy(this._quat);
      this._lastTime = timeSec;
      this._hasPrev = true;
      this._compose(this._pos, this._quat, this._scale, dt0);
      return;
    }

    // Scale: component filter is fine (scale changes are slow and uncoupled).
    const sx = this.sx.filter(this._scale.x, timeSec);
    const sy = this.sy.filter(this._scale.y, timeSec);
    const sz = this.sz.filter(this._scale.z, timeSec);

    const dt = Math.max(1e-3, timeSec - this._lastTime);
    const o = mapResponsiveness(this._resp);

    // ── Rotation: adaptive SLERP (preserves rotation angle; tracks fast turns) ──
    if (!this._hasPrev) {
      this._qOut.copy(this._quat);
    } else {
      let dot = this._qOut.x*this._quat.x + this._qOut.y*this._quat.y
              + this._qOut.z*this._quat.z + this._qOut.w*this._quat.w;
      dot = Math.min(1, Math.abs(dot));
      const theta = 2 * Math.acos(dot);                 // rad since last
      const speed = theta / dt;
      const cutoff = o.mincutoff + o.beta * speed * 60;
      const tau = 1 / (2 * Math.PI * cutoff);
      const t = Math.min(1, Math.max(0, 1 / (1 + tau / dt)));
      this._qOut.slerp(this._quat, t);
    }

    // ── Translation: adaptive LERP with the SAME speed-adaptive factor scheme ──
    // The old per-axis One-Euro filters damped fast motion, so when the head
    // turned and the nose bridge translated across the frame, the glasses lagged
    // (moved "very little" vs the real bridge). Using a speed-adaptive lerp that
    // mirrors the rotation slerp keeps position and orientation in LOCKSTEP, so
    // they can't diverge — and fast moves are followed ~1:1 while rest is smooth.
    if (!this._hasPrev) {
      this._posOut.copy(this._pos);
    } else {
      const posSpeed = this._pos.distanceTo(this._posOut) / dt;  // cm/s
      const cutoffP = o.mincutoff + o.beta * posSpeed * 6;
      const tauP = 1 / (2 * Math.PI * cutoffP);
      const tp = Math.min(1, Math.max(0, 1 / (1 + tauP / dt)));
      this._posOut.lerp(this._pos, tp);
    }

    this._scale.set(sx, sy, sz);
    this._updateVelocity(dt);
    this._quatPrev.copy(this._quat);
    this._lastTime = timeSec;
    this._hasPrev = true;

    this._compose(this._posOut, this._qOut, this._scale, dt);
  }

  // Track RAW pose velocity (before smoothing, so prediction is not itself
  // delayed), lightly low-passed so noise isn't amplified by extrapolation.
  _updateVelocity(dt) {
    if (this._hasPrev && dt > 0 && dt < 0.25) {
      // linear
      const vx = (this._pos.x - this._rawPosPrev.x) / dt;
      const vy = (this._pos.y - this._rawPosPrev.y) / dt;
      const vz = (this._pos.z - this._rawPosPrev.z) / dt;
      this._vel.x += (vx - this._vel.x) * 0.5;
      this._vel.y += (vy - this._vel.y) * 0.5;
      this._vel.z += (vz - this._vel.z) * 0.5;
      // angular: per-frame delta dq = q · prev⁻¹ (world-space), sign-stabilised
      this._qTmp.copy(this._rawQuatPrev).invert().premultiply(this._quat);
      if (this._qTmp.w < 0) { this._qTmp.x*=-1; this._qTmp.y*=-1; this._qTmp.z*=-1; this._qTmp.w*=-1; }
      this._dq.slerp(this._qTmp, 0.5);
      this._dtAvg += (dt - this._dtAvg) * 0.2;
    } else {
      this._vel.set(0, 0, 0);
      this._dq.identity();
    }
    this._rawPosPrev.copy(this._pos);
    this._rawQuatPrev.copy(this._quat);
  }

  _compose(pos, quat, scale, dt) {
    const lead = this._leadSec;
    if (lead > 0 && this._hasPrev) {
      this._posPred.copy(this._vel).multiplyScalar(lead).add(pos);
      // rotation: extrapolate the per-frame delta by lead/dt frames (slerp with
      // t > 1 is a valid geodesic extrapolation; capped at 3 frames for safety)
      const tFrames = Math.min(3, lead / Math.max(1e-3, this._dtAvg));
      this._qPred.copy(this._qIdentity).slerp(this._dq, tFrames).multiply(quat);
      this._out.compose(this._posPred, this._qPred, scale);
    } else {
      this._out.compose(pos, quat, scale);
    }
  }

  getMatrix() { return this._out; }
  getTranslation() { return { x: this._posOut.x, y: this._posOut.y, z: this._posOut.z }; }

  /**
   * The glasses are posed with the FULL smoothed head pose — no pitch damping.
   *
   * An earlier version scaled the head pitch by a fraction to stop the frame
   * riding up on look-down. That was wrong: a fixed fraction creates a PERMANENT
   * angular error (the frame never catches up to a held head angle) and the
   * under-pitch throws the temple arms onto the forehead when looking down. The
   * frame must track the head 1:1 so it always catches up. The natural resting
   * tilt is handled instead by a fixed PANTOSCOPIC TILT baked into the rest pose
   * (see Glasses._rebuildShift), which rotates with the head and adds no offset.
   */
  getGlassesMatrix() {
    return this._out;
  }
}
